import React, { forwardRef, useMemo } from 'react'

import { twMerge } from 'tailwind-merge'

import Spinner from '@/components/common/Spinner'

import type { Variant } from '@/types/components/Variant'

export interface ButtonProps extends Omit<
  React.HTMLProps<HTMLButtonElement>,
  'type'
> {
  type?: 'button' | 'submit' | 'reset'
  variant?: Variant
  isLoading?: boolean
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, className, disabled, isLoading = false, ...props }, ref) => {
    let tailwindClasses: string
    switch (props.variant) {
      case 'outline':
        tailwindClasses =
          'inline-flex whitespace-nowrap items-center justify-center rounded-md gap-x-1.5 px-2 h-[34px] bg-white dark:bg-inherit border border-primary-300 dark:border-primary-700 text-primary-800 dark:text-primary-200 transition duration-100'
        break
      case 'outline-compact':
        tailwindClasses =
          'inline-flex whitespace-nowrap items-center justify-center rounded-[5px] gap-x-1.5 px-2 h-7 bg-white dark:bg-inherit border border-primary-300 dark:border-primary-700 font-medium text-primary-800 dark:text-primary-200 transition duration-100'
        break
      case 'soft':
        tailwindClasses =
          'inline-flex whitespace-nowrap items-center justify-center rounded-md gap-x-1.5 px-2 h-[34px] border border-primary-300 dark:border-primary-700 text-primary-800 dark:text-primary-200 transition duration-100'
        break
      case 'soft-compact':
        tailwindClasses =
          'inline-flex whitespace-nowrap items-center justify-center rounded-[5px] gap-x-1.5 px-2 h-7 border border-primary-300 dark:border-primary-700 font-medium text-primary-800 dark:text-primary-200 transition duration-100'
        break
      // The professional theme drops the solid button a full step down the
      // ramp, to the deep navy the rail is painted in: white on `accent-400`
      // is 3.8:1, on `accent-600` it is 8.4:1. Banana is not spent here — a
      // page of yellow buttons is what makes yellow stop meaning anything. It
      // goes on the `yellow` variant below, which is already the app's "this
      // one is different" button.
      case 'primary':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-md gap-x-1.5 px-2 h-[34px] text-white font-medium bg-accent-400 transition duration-100 professional:bg-accent-600 professional:font-semibold'
        break
      case 'primary-compact':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-[5px] gap-x-1.5 px-2 h-7 font-medium text-white bg-accent-400 dark:bg-accent-400/80 border border-accent-500 transition duration-100 professional:bg-accent-600 professional:border-accent-700 professional:font-semibold'
        break
      case 'danger':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-md gap-x-1.5 px-2 h-[34px] text-white bg-red-600 dark:bg-red-500 transition duration-100'
        break
      case 'danger-compact':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-[5px] gap-x-1.5 px-2 h-7 font-medium text-white bg-red-600 dark:bg-red-600/80 transition duration-100 border border-red-700 dark:border-red-700'
        break
      case 'positive':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-md gap-x-1.5 px-2 h-[34px] text-green-900 dark:text-white bg-green-50 dark:bg-green-600/50 transition duration-100 border border-green-400 dark:border-green-700'
        break
      case 'positive-compact':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-[5px] gap-x-1.5 px-2 h-7 font-medium text-green-900 dark:text-white bg-green-600 dark:bg-green-600/60 border border-green-700 text-white transition duration-100'
        break
      case 'blue':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-md gap-x-1.5 px-2 h-[34px] text-blue-900 dark:text-white bg-blue-50 dark:bg-blue-600/60 transition duration-100 border border-blue-400 dark:border-blue-700'
        break
      case 'blue-compact':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-[5px] gap-x-1.5 px-2 h-7 font-medium text-blue-900 dark:text-white bg-blue-600 dark:bg-blue-600/70 border border-blue-700 dark:border-blue-600 text-white transition duration-100'
        break
      case 'ghost':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-md gap-x-1.5 px-2 h-[34px] text-primary-900 dark:text-primary-100 bg-transparent transition duration-100 border border-transparent'
        break
      case 'ghost-compact':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-[5px] gap-x-1.5 px-2 h-7 font-medium text-primary-900 dark:text-primary-100 bg-transparent transition duration-100 border border-transparent'
        break
      // in the professional theme the yellow variant is the real banana,
      // filled rather than tinted, because this is the one button the theme
      // wants you to look at first
      case 'yellow':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-md gap-x-1.5 px-2 h-[34px] text-yellow-900 dark:text-white bg-yellow-50 dark:bg-yellow-600/50 transition duration-100 border border-yellow-400 dark:border-yellow-700 professional:bg-banana-400 professional:text-primary-900 professional:font-semibold professional:border-banana-500'
        break
      case 'yellow-compact':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-[5px] gap-x-1.5 px-2 h-7 font-medium text-yellow-900 dark:text-white bg-yellow-600 dark:bg-yellow-600/50 border border-yellow-700 text-white transition duration-100 professional:bg-banana-400 professional:text-primary-900 professional:font-semibold professional:border-banana-500'
        break
      case 'teal':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-md gap-x-1.5 px-2 h-[34px] text-white bg-teal-600 dark:bg-teal-500 transition duration-100 border border-teal-700 dark:border-teal-700'
        break
      case 'teal-compact':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-[5px] gap-x-1.5 px-2 h-7 font-medium text-white bg-teal-600 dark:bg-teal-500 border border-teal-700 dark:border-teal-700 transition duration-100'
        break
      case 'green':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-md gap-x-1.5 px-2 h-[34px] text-white bg-green-600 dark:bg-green-500 transition duration-100 border border-green-700 dark:border-green-700'
        break
      case 'green-compact':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-[5px] gap-x-1.5 px-2 h-7 font-medium text-white bg-green-600 dark:bg-green-600/80 border border-green-700 dark:border-green-700 transition duration-100'
        break
      case 'indigo':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-md gap-x-1.5 px-2 h-[34px] text-white bg-indigo-600 dark:bg-indigo-500 transition duration-100 border border-indigo-700 dark:border-indigo-700'
        break
      case 'indigo-compact':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-[5px] gap-x-1.5 px-2 h-7 font-medium text-white bg-indigo-600 dark:bg-indigo-500 border border-indigo-700 dark:border-indigo-700 transition duration-100'
        break
      case 'amber':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-md gap-x-1.5 px-2 h-[34px] text-white bg-amber-500 dark:bg-amber-600 transition duration-100 border border-amber-700 dark:border-amber-700'
        break
      case 'amber-compact':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-[5px] gap-x-1.5 px-2 h-7 font-medium text-white bg-amber-600 dark:bg-amber-700 border border-amber-600 dark:border-amber-600 transition duration-100'
        break
      case 'purple':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-md gap-x-1.5 px-2 h-[34px] text-white bg-purple-600 dark:bg-purple-500 transition duration-100 border border-purple-700 dark:border-purple-700'
        break
      case 'purple-compact':
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-[5px] gap-x-1.5 px-2 h-7 font-medium text-white bg-purple-600 dark:bg-purple-600/60 border border-purple-700 dark:border-purple-600 transition duration-100'
        break
      default:
        tailwindClasses =
          'flex whitespace-nowrap items-center justify-center rounded-md gap-x-1.5 px-2 h-[34px] text-white bg-accent-400 transition duration-100'
    }

    let spinnerTailwindClasses: string
    switch (props.variant) {
      case 'danger':
        spinnerTailwindClasses =
          'fill-red-700 america:fill-red-700 dark:fill-red-700 text-red-200 america:!text-red-200 dark:text-red-200'
        break
      case 'danger-compact':
        spinnerTailwindClasses =
          'fill-red-700 america:fill-red-700 dark:fill-red-700 text-red-200 america:!text-red-200 dark:text-red-200'
        break
      case 'outline':
        spinnerTailwindClasses =
          'fill-primary-600 america:fill-primary-600 dark:fill-primary-400'
        break
      case 'outline-compact':
        spinnerTailwindClasses =
          'fill-primary-600 america:fill-primary-600 dark:fill-primary-400'
        break
      case 'soft':
        spinnerTailwindClasses =
          'fill-primary-600 america:fill-primary-600 dark:fill-primary-400'
        break
      case 'soft-compact':
        spinnerTailwindClasses =
          'fill-primary-600 america:fill-primary-600 dark:fill-primary-400'
        break
      // Both halves of this spinner had come out the button's own navy under
      // the professional theme, leaving a blank pill while a form submitted.
      // The arc, because the standalone spinner paints itself `accent-600` on
      // a `professional:` modifier that this string had no counterpart for, so
      // it outranked the plain `fill-white` — a modifier only loses to another
      // modifier. The track, because `accent-600` is exactly what the theme
      // paints the button. Stating both for the theme puts the button back in
      // charge: the white arc it has everywhere else, over a track one step up
      // the ramp, the same read the other themes get from 600 on 400.
      case 'primary':
        spinnerTailwindClasses =
          'fill-white america:fill-white professional:fill-white dark:fill-primary-300 text-accent-600 professional:text-accent-400 dark:text-accent-600'
        break
      case 'primary-compact':
        spinnerTailwindClasses =
          'fill-white america:fill-white professional:fill-white dark:fill-primary-100 text-accent-600 professional:text-accent-400 dark:text-accent-400/60'
        break
      case 'positive':
        spinnerTailwindClasses =
          'fill-green-100 america:fill-green-100 dark:fill-green-100 text-green-700 america:!text-green-700 dark:text-green-700'
        break
      case 'positive-compact':
        spinnerTailwindClasses =
          'fill-green-100 america:fill-green-100 dark:fill-green-300 text-green-700 america:!text-green-700 dark:text-green-700'
        break
      case 'blue':
        spinnerTailwindClasses =
          'fill-blue-100 america:fill-blue-100 dark:fill-blue-300 text-blue-700 america:!text-blue-700 dark:text-blue-700'
        break
      case 'blue-compact':
        spinnerTailwindClasses =
          'fill-blue-100 america:fill-blue-100 dark:fill-blue-300 text-blue-700 america:!text-blue-700 dark:text-blue-700'
        break
      case 'ghost':
        spinnerTailwindClasses =
          'fill-white america:fill-white dark:fill-primary-300 text-primary-800 dark:text-primary-200'
        break
      case 'ghost-compact':
        spinnerTailwindClasses =
          'fill-white america:fill-white dark:fill-primary-300 text-primary-800 dark:text-primary-200'
        break
      case 'yellow':
        spinnerTailwindClasses =
          'fill-yellow-100 america:fill-yellow-100 dark:fill-yellow-300 text-yellow-700 america:!text-yellow-700 dark:text-yellow-700'
        break
      case 'yellow-compact':
        spinnerTailwindClasses =
          'fill-yellow-100 america:fill-yellow-100 dark:fill-yellow-300 text-yellow-700 america:!text-yellow-700 dark:text-yellow-700'
        break
      case 'teal':
      case 'teal-compact':
        spinnerTailwindClasses =
          'fill-teal-100 america:fill-teal-100 dark:fill-teal-300 text-teal-700 america:!text-teal-700 dark:text-teal-700'
        break
      case 'green':
      case 'green-compact':
        spinnerTailwindClasses =
          'fill-green-100 america:fill-green-100 dark:fill-green-300 text-green-700 america:!text-green-700 dark:text-green-700'
        break
      case 'indigo':
      case 'indigo-compact':
        spinnerTailwindClasses =
          'fill-indigo-100 america:fill-indigo-100 dark:fill-indigo-300 text-indigo-700 america:!text-indigo-700 dark:text-indigo-700'
        break
      case 'amber':
      case 'amber-compact':
        spinnerTailwindClasses =
          'fill-amber-100 america:fill-amber-100 dark:fill-amber-300 text-amber-700 america:!text-amber-700 dark:text-amber-700'
        break
      case 'purple':
      case 'purple-compact':
        spinnerTailwindClasses =
          'fill-purple-100 america:fill-purple-100 dark:fill-purple-300 text-purple-700 america:!text-purple-700 dark:text-purple-700'
        break
      default:
        spinnerTailwindClasses =
          'fill-white america:fill-white dark:fill-primary-300'
    }

    const isCompact =
      props.variant === 'danger-compact' ||
      props.variant === 'outline-compact' ||
      props.variant === 'primary-compact' ||
      props.variant === 'positive-compact' ||
      props.variant === 'soft-compact' ||
      props.variant === 'blue-compact' ||
      props.variant === 'ghost-compact' ||
      props.variant === 'yellow-compact' ||
      props.variant === 'teal-compact' ||
      props.variant === 'green-compact' ||
      props.variant === 'indigo-compact' ||
      props.variant === 'amber-compact' ||
      props.variant === 'purple-compact'

    const childrenWithStyles = useMemo(
      () =>
        // Only apply opacity styles when loading - otherwise let Tailwind classes work
        isLoading
          ? React.Children.map(children, (child) => {
              const addOpacity = (child: React.ReactNode): React.ReactNode => {
                if (React.isValidElement(child)) {
                  return React.cloneElement(child, {
                    ...child.props,
                    style: {
                      ...child.props.style,
                      opacity: 0,
                      transition: 'opacity 0.2s',
                    },
                    children: React.Children.map(
                      child.props.children,
                      addOpacity,
                    ),
                  })
                }
                if (typeof child === 'string' || typeof child === 'number') {
                  return (
                    <span
                      style={{
                        opacity: 0,
                        transition: 'opacity 0.2s',
                      }}
                    >
                      {child}
                    </span>
                  )
                }
                return child
              }
              return addOpacity(child)
            })
          : children,
      [children, isLoading],
    )

    return (
      <button
        ref={ref}
        disabled={disabled}
        className={twMerge(
          tailwindClasses,
          'relative select-none text-sm',
          disabled ? 'cursor-not-allowed opacity-60' : 'active:scale-95',
          !disabled &&
            (props.variant === 'outline' || props.variant === 'outline-compact')
            ? 'hover:border-primary-400 dark:hover:border-primary-600'
            : '',
          !disabled &&
            (props.variant === 'soft' ||
              props.variant === 'soft-compact' ||
              props.variant === 'ghost' ||
              props.variant === 'ghost-compact')
            ? 'transition-colors duration-100 hover:bg-primary-100 dark:hover:bg-primary-800'
            : '',
          !disabled &&
            props.variant !== 'outline' &&
            props.variant !== 'outline-compact' &&
            props.variant !== 'soft' &&
            props.variant !== 'soft-compact' &&
            props.variant !== 'ghost' &&
            props.variant !== 'ghost-compact'
            ? 'hover:opacity-80 dark:hover:opacity-80'
            : '',
          className ?? '',
        )}
        {...props}
      >
        {isLoading && (
          <div className="absolute">
            <Spinner
              className={twMerge(
                isCompact ? 'w-4 h-4' : 'w-5 h-5',
                spinnerTailwindClasses,
              )}
            />
          </div>
        )}
        {childrenWithStyles}
      </button>
    )
  },
)

Button.displayName = 'Button'

export default Button
